import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../constants/appConstants';

export interface JiraBoardColumn {
  id: string;
  title: string;
  tone: string;
  statusNames: string[];
}

export interface JiraBoardStatus {
  id: string;
  name: string;
  statusCategory: string;
  color: string;
}

interface UseJiraBoardConfigResult {
  /** Saved columns, or null when none configured (fall back to categories). */
  columns: JiraBoardColumn[] | null;
  /** All Jira statuses available for assignment. */
  statuses: JiraBoardStatus[];
  loading: boolean;
  error: string | null;
  save: (columns: JiraBoardColumn[]) => Promise<JiraBoardColumn[]>;
  clear: () => Promise<void>;
  refetch: () => Promise<void>;
}

/**
 * Admin hook for the org-wide Jira board column configuration.
 * Wraps GET / PUT / DELETE /api/jira/board-config.
 */
export const useJiraBoardConfig = (): UseJiraBoardConfigResult => {
  const [columns, setColumns] = useState<JiraBoardColumn[] | null>(null);
  const [statuses, setStatuses] = useState<JiraBoardStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/jira/board-config`, {
        withCredentials: true
      });
      setColumns(response.data.columns || null);
      setStatuses(response.data.statuses || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load Jira board config');
      setColumns(null);
      setStatuses([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const save = useCallback(async (next: JiraBoardColumn[]) => {
    setError(null);
    const response = await axios.put(
      `${API_URL}/jira/board-config`,
      { columns: next },
      { withCredentials: true }
    );
    const saved = response.data.columns || next;
    setColumns(saved);
    return saved;
  }, []);

  const clear = useCallback(async () => {
    await axios.delete(`${API_URL}/jira/board-config`, { withCredentials: true });
    setColumns(null);
  }, []);

  return { columns, statuses, loading, error, save, clear, refetch };
};

export default useJiraBoardConfig;
